import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus } from '@phosphor-icons/react';
import Layout from '../../components/layout/Layout';
import { profileAPI } from '../../utils/api';
import { trackSubjectAdded } from '../../utils/analytics';
import WalkthroughTour from '../../components/walkthrough/WalkthroughTour';
import { addSubjectSteps } from '../../components/walkthrough/tourSteps';
import { useAuth } from '../../contexts/AuthContext';
import { shouldShowTour } from '../../config/tourConfig';
import InfoIcon from '../../components/common/InfoIcon';

export default function AddSubjectPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    subject_name: '',
    course_name: '',
    school_name: '',
    syllabus_url: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSyllabusPopup, setShowSyllabusPopup] = useState(false);
  const [suggestionData, setSuggestionData] = useState<{
    suggestion: string;
    course_id: string;
  } | null>(null);
  const [syllabusUrl, setSyllabusUrl] = useState('');
  const [showManualSyllabusOption, setShowManualSyllabusOption] = useState(false);

  // PDF upload state
  const [uploadMode, setUploadMode] = useState<'url' | 'file'>('url');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Focus state for helper text
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Tour integration
  const { userProfile, refreshProfile } = useAuth();
  const [showTour, setShowTour] = useState(false);

  // Determine if we should show the add-subject tour
  useEffect(() => {
    if (userProfile && shouldShowTour(userProfile.tour_add_subject, userProfile.tour_enabled, 'add-subject')) {
      setShowTour(true);
    }
  }, [userProfile]);

  // Stable callback references for tour handlers
  const handleTourComplete = useCallback(() => {
    setShowTour(false);
    refreshProfile();
  }, [refreshProfile]);

  const handleTourSkip = useCallback(() => {
    setShowTour(false);
    refreshProfile();
  }, [refreshProfile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
  };

  const handleModeChange = (mode: 'url' | 'file') => {
    setUploadMode(mode);
    setError(null);
    // Clear the opposite mode's data
    if (mode === 'url') {
      setSelectedFile(null);
    } else {
      setFormData(prev => ({ ...prev, syllabus_url: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.subject_name || !formData.course_name) {
      setError('Subject name and course name are required');
      return;
    }

    // Validate that only one syllabus source is provided
    if (formData.syllabus_url && selectedFile) {
      setError('Please provide either a URL or a PDF file, not both');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submissionData = {
        ...formData,
        ...(selectedFile && { syllabus_file: selectedFile })
      };

      const response = await profileAPI.addSubjectCourse(submissionData);

      // Check if we need to show the syllabus popup
      if (response.suggestion && response.suggestion.includes('unable to retrieve the course syllabus')) {
        setSuggestionData({
          suggestion: response.suggestion,
          course_id: response.course_id
        });
        setShowSyllabusPopup(true);
      } else {
        // Navigate back to dashboard with refresh flag
        trackSubjectAdded(formData.subject_name);
        navigate('/dashboard', { state: { fromAddSubject: true } });
      }
    } catch (err: any) {
      console.error('Failed to add subject:', err);
      setError(err.message || 'Failed to add subject');
    } finally {
      setLoading(false);
    }
  };

  const handleSyllabusUrlSubmit = async () => {
    if (!syllabusUrl || !suggestionData) return;

    try {
      setLoading(true);
      const response = await profileAPI.updateSyllabusFromUrl({
        course_id: suggestionData.course_id,
        syllabus_url: syllabusUrl
      });
      
      if (response.success) {
        setShowSyllabusPopup(false);
        navigate('/dashboard', { state: { fromAddSubject: true } });
      } else {
        // URL didn't work, show manual syllabus option
        setShowManualSyllabusOption(true);
      }
    } catch (err: any) {
      console.error('Failed to update syllabus:', err);
      // URL didn't work, show manual syllabus option
      setShowManualSyllabusOption(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipSyllabus = () => {
    setShowSyllabusPopup(false);
    navigate('/dashboard', { state: { fromAddSubject: true } });
  };

  const handleManualSyllabus = () => {
    if (suggestionData) {
      // Navigate to manual syllabus page
      navigate(`/dashboard/add-syllabus/${suggestionData.course_id}`);
    }
  };

  return (
    <Layout>
      <div className="px-4 md:px-8 lg:px-40 flex flex-1 justify-center py-5">
        <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-white hover:text-text-secondary transition-colors"
            >
              <ArrowLeft size={24} weight="regular" />
            </button>
            <h1 className="text-white text-2xl font-bold">Add New Subject</h1>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div data-tour="subject-form" className="bg-input-bg border border-border-light rounded-lg p-6">
              <h2 className="text-white text-lg font-semibold mb-4">Basic Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Subject Name *
                    <InfoIcon 
                      title="Subject Name"
                      content='Just for your reference (e.g., "Math", "Science"). This helps you identify the subject on your dashboard.'
                    />
                  </label>
                  <input
                    type="text"
                    name="subject_name"
                    value={formData.subject_name}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('subject_name')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full px-3 py-2 bg-background border border-border-light rounded text-white placeholder-text-secondary focus:outline-none focus:border-primary"
                    placeholder="e.g., Math, Science, English"
                    required
                  />
                  {focusedField === 'subject_name' && (
                    <p className="mt-1 text-xs text-text-secondary">For your reference - helps you identify the subject</p>
                  )}
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Course Name *
                    <InfoIcon 
                      title="Course Name"
                      content='Official course name (e.g., "AP Calculus", "IB AA Mathematics HL", "Math 100"). This helps us find the right syllabus and course content.'
                    />
                  </label>
                  <input
                    type="text"
                    name="course_name"
                    value={formData.course_name}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('course_name')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full px-3 py-2 bg-background border border-border-light rounded text-white placeholder-text-secondary focus:outline-none focus:border-primary"
                    placeholder="e.g., AP Calculus BC, IB Math HL, CHEM 101"
                    required
                  />
                  {focusedField === 'course_name' && (
                    <p className="mt-1 text-xs text-text-secondary">Official course name - helps us find the right syllabus</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    School Name (Optional)
                    <InfoIcon 
                      title="School Name"
                      content='Helps us find the syllabus for generic course names like "Math 100". Include your school or university name for more accurate results.'
                    />
                  </label>
                  <input
                    type="text"
                    name="school_name"
                    value={formData.school_name}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('school_name')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full px-3 py-2 bg-background border border-border-light rounded text-white placeholder-text-secondary focus:outline-none focus:border-primary"
                    placeholder="e.g., Harvard University, MIT, UCLA"
                  />
                  {focusedField === 'school_name' && (
                    <p className="mt-1 text-xs text-text-secondary">Helps find syllabus for generic course names</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-white text-sm font-medium mb-2">
                    Syllabus (Optional)
                    <InfoIcon 
                      title="Syllabus"
                      content="Provide a syllabus URL or upload a PDF. We'll automatically extract course topics and objectives to make sure we teach you everything you need to learn for your course."
                    />
                  </label>

                  {/* Toggle between URL and File */}
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => handleModeChange('url')}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        uploadMode === 'url'
                          ? 'bg-primary text-white'
                          : 'bg-input-bg border border-border-light text-text-secondary hover:bg-border'
                      }`}
                    >
                      URL
                    </button>
                    <button
                      type="button"
                      onClick={() => handleModeChange('file')}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        uploadMode === 'file'
                          ? 'bg-primary text-white'
                          : 'bg-input-bg border border-border-light text-text-secondary hover:bg-border'
                      }`}
                    >
                      Upload PDF
                    </button>
                  </div>

                  {uploadMode === 'url' ? (
                    <input
                      type="url"
                      name="syllabus_url"
                      value={formData.syllabus_url}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-background border border-border-light rounded text-white placeholder-text-secondary focus:outline-none focus:border-primary"
                      placeholder="https://example.com/syllabus.pdf"
                    />
                  ) : (
                    <div>
                      {!selectedFile ? (
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border-light rounded-lg cursor-pointer hover:border-primary hover:bg-input-bg/50 transition-colors">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <svg className="w-8 h-8 mb-3 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <p className="mb-2 text-sm text-text-secondary">
                              <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-text-secondary">PDF files only</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,application/pdf"
                            onChange={handleFileSelect}
                          />
                        </label>
                      ) : (
                        <div className="flex items-center justify-between p-3 bg-background border border-border-light rounded">
                          <div className="flex items-center gap-3">
                            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <div>
                              <p className="text-white text-sm font-medium">{selectedFile.name}</p>
                              <p className="text-text-secondary text-xs">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleClearFile}
                            className="text-red-400 hover:text-red-300 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2 bg-transparent border border-border-light text-white rounded hover:bg-input-bg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Adding...' : 'Add Subject'}
              </button>
            </div>
          </form>

          {/* Syllabus URL Popup */}
          {showSyllabusPopup && suggestionData && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-input-bg border border-border-light rounded-lg p-6 max-w-md w-full mx-4">
                <h3 className="text-white text-lg font-semibold mb-4">
                  Syllabus Information Needed
                </h3>
                
                <p className="text-text-secondary text-sm mb-4">
                  {suggestionData.suggestion}
                </p>

                {!showManualSyllabusOption ? (
                  <>
                    <div className="mb-4">
                      <label className="block text-white text-sm font-medium mb-2">
                        Syllabus URL
                      </label>
                      <input
                        type="url"
                        value={syllabusUrl}
                        onChange={(e) => setSyllabusUrl(e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-border-light rounded text-white placeholder-text-secondary focus:outline-none focus:border-primary"
                        placeholder="https://example.com/syllabus.pdf"
                      />
                    </div>

                    <div className="flex gap-3 justify-end">
                      <button
                        type="button"
                        onClick={handleSkipSyllabus}
                        className="px-4 py-2 bg-transparent border border-border-light text-white rounded hover:bg-input-bg/50 transition-colors"
                        disabled={loading}
                      >
                        Skip for Now
                      </button>
                      <button
                        type="button"
                        onClick={handleSyllabusUrlSubmit}
                        disabled={loading || !syllabusUrl}
                        className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Updating...' : 'Update Syllabus'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-text-secondary text-sm mb-4">
                      The provided URL couldn't be processed. You can either try a different URL or provide syllabus information manually.
                    </p>

                    <div className="mb-4">
                      <label className="block text-white text-sm font-medium mb-2">
                        Try a Different Syllabus URL
                      </label>
                      <input
                        type="url"
                        value={syllabusUrl}
                        onChange={(e) => setSyllabusUrl(e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-border-light rounded text-white placeholder-text-secondary focus:outline-none focus:border-primary"
                        placeholder="https://example.com/different-syllabus.pdf"
                      />
                    </div>

                    <div className="flex gap-3 justify-end">
                      <button
                        type="button"
                        onClick={handleSkipSyllabus}
                        className="px-4 py-2 bg-transparent border border-border-light text-white rounded hover:bg-input-bg/50 transition-colors"
                        disabled={loading}
                      >
                        Skip for Now
                      </button>
                      <button
                        type="button"
                        onClick={handleManualSyllabus}
                        className="px-4 py-2 bg-secondary text-white rounded hover:bg-secondary/90 transition-colors"
                        disabled={loading}
                      >
                        Add Manually
                      </button>
                      <button
                        type="button"
                        onClick={handleSyllabusUrlSubmit}
                        disabled={loading || !syllabusUrl}
                        className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Trying...' : 'Try URL'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contextual Tour */}
      {showTour && (
        <WalkthroughTour
          section="add-subject"
          steps={addSubjectSteps}
          isActive={true}
          onComplete={handleTourComplete}
          onSkip={handleTourSkip}
        />
      )}
    </Layout>
  );
}
